import { useState } from 'react';
import { authClient } from '@/lib/auth-client';

/**
 * Google's mark, inlined. lucide carries no brand icons, and a remote asset
 * would be one more thing that can fail on the one page whose entire job is to
 * load and let someone in.
 */
function GoogleMark() {
  return (
    <svg className="size-4" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * Identity only — this grant asks for `openid email profile` and nothing else.
 * Connecting a mailbox is a separate consent with its own restricted scopes,
 * so signing in never demands access to anyone's mail.
 */
export default function GoogleSignInButton({
  label,
  onError,
}: {
  label: string;
  onError: (message: string) => void;
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/`,
        // A refusal — an address that is not on the allowlist — comes back
        // here rather than dumping the person on a raw OAuth error page.
        errorCallbackURL: `${window.location.origin}/login`,
      });
      // Success navigates away to Google. Still being here means it did not.
    } catch {
      onError('Could not reach Google. Check your connection and try again.');
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-6 text-sm font-medium transition-colors hover:bg-[var(--color-surface-container-high)] disabled:pointer-events-none disabled:opacity-50"
    >
      <GoogleMark />
      {loading ? 'Redirecting to Google…' : label}
    </button>
  );
}
